import React, { useState, useEffect, useRef } from "react";
import { searchPatients } from "../../api";
import { jwtDecode } from "jwt-decode";
import { Form, InputGroup } from "react-bootstrap";

export function PatientSearch({ onPatientSelect }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [practices, setPractices] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const skipNextSearchRef = useRef(false);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }

    if (searchQuery.trim() === "") {
      setResults([]);
      return;
    }

    let cancelled = false;

    const fetchResults = async () => {
      const data = await searchPatients(searchQuery, practices);
      if (!cancelled) setResults(data || []);
    };

    const t = setTimeout(fetchResults, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [searchQuery, practices]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    const token = sessionStorage.getItem("user");
    if (token) {
      const decoded = jwtDecode(token);
      setPractices(decoded.practice || []);
    }
    setSelectedPatient(null);
  };

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    onPatientSelect?.(patient);
    skipNextSearchRef.current = true;
    setSearchQuery(
      `${patient.patientFirstName} ${patient.patientLastName} ${patient.patientNumber ?? ""}`.trim()
    );
    setResults([]);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setResults([]);
    setSelectedPatient(null);
    onPatientSelect?.(null);
  };

  return (
    <div style={{ position: "relative" }}>
      <InputGroup className="flex-nowrap">
        <InputGroup.Text
          className="flex-shrink-0"
          style={{
            backgroundColor: "rgb(206.6, 226, 254.6)",
            border: "1px solid rgb(206.6, 226, 254.6)",
            borderRight: "none",
          }}
        >
          <i className="fa-light fa-user-magnifying-glass text-primary" />
        </InputGroup.Text>

        <Form.Control
          type="text"
          placeholder="Search by patient first name, last name, ID number, or medical aid number"
          value={searchQuery}
          onChange={handleSearchChange}
          style={{ borderLeft: "none", height: "38px", minWidth: 0 }}
        />

        {searchQuery && (
          <InputGroup.Text
            onClick={clearSearch}
            className="flex-shrink-0"
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #ced4da",
              borderLeft: "none",
              cursor: "pointer",
            }}
          >
            <i className="fa-solid fa-times text-muted" />
          </InputGroup.Text>
        )}
      </InputGroup>

      {results.length > 0 && (
        <div
          className="results-box"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ced4da",
            borderTop: "none",
            borderRadius: "0 0 0.375rem 0.375rem",
            boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {results.map((p) => {
            // Determine patient type badge
            const getBadge = () => {
              if (p.patientIODFund) {
                const isInsurance =
                  (p.patientIODFundCategory || "").toUpperCase() === "INSURANCE";
                return (
                  <span
                    className={`badge ${isInsurance ? "bg-info" : "bg-warning"} text-dark`}
                  >
                    {isInsurance ? "INS" : "IOD"}
                  </span>
                );
              }
              if (p.patientMedicalAid) {
                return (
                  <span className="badge bg-primary">
                    Medical Aid
                  </span>
                );
              }
              return (
                <span className="badge bg-secondary">
                  Private
                </span>
              );
            };

            return (
              <div
                key={p._id}
                className="result-item d-flex justify-content-between align-items-center"
                onClick={() => handlePatientClick(p)}
                onTouchEnd={(e) => { e.preventDefault(); handlePatientClick(p); }}
                style={{
                  cursor: "pointer",
                  padding: "0.5rem 0.75rem",
                  borderBottom: "1px solid #f8f9fa",
                  fontSize: "0.875rem",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                  e.currentTarget.style.color = "#0F3490";
                  e.currentTarget.style.fontWeight = "bold";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                  e.currentTarget.style.color = "black";
                  e.currentTarget.style.fontWeight = "normal";
                }}
              >
                <div>
                  <div>
                    <strong>{p.patientNumber}</strong> — {p.patientFirstName} {p.patientLastName}
                  </div>
                  <small className="text-muted">
                    ID: {p.patientIdNumber || "—"}
                  </small>
                </div>
                {getBadge()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PatientSearch;
